import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PostEducationHistoryResponseBody } from '@ab/ams-school-api/types/private/v1';
import { config } from '~/config';
import { hasKey } from '~/utils';
import { useRequest } from '~/lib/useRequest';
import { useS3Upload } from './useS3Upload';

type FileInput = {
  fileObject: File;
  fileName: string;
  contentType: string;
};

export type CreateEducationHistoryParams = {
  studentId: string;
  applicationId: string;
  schoolName: string;
  educationLevel: string;
  educationCountry: string;
  credentialReceived: string;
  startDate: string;
  endDate: string;
  degree?: string;
  fieldOfStudy?: string;
  gradeSchemeId?: string;
  gradeSchemeName?: string;
  transcriptFile?: FileInput;
  certificateFile?: FileInput;
};

export function useCreateEducationHistory() {
  const request = useRequest();
  const queryClient = useQueryClient();
  const { s3UploadAsync } = useS3Upload();

  const { isLoading, error, mutateAsync } = useMutation({
    mutationKey: ['createEducationHistory'],
    mutationFn: async (params: CreateEducationHistoryParams) => {
      const transcriptPayload = params.transcriptFile
        ? {
            fileName: params.transcriptFile.fileName,
            contentType: params.transcriptFile.contentType,
            fileSize: params.transcriptFile.fileObject.size,
          }
        : undefined;

      const certificatePayload = params.certificateFile
        ? {
            fileName: params.certificateFile.fileName,
            contentType: params.certificateFile.contentType,
            fileSize: params.certificateFile.fileObject.size,
          }
        : undefined;

      const response = await request<PostEducationHistoryResponseBody>(
        `${config.apiHost}/private/v1/students/${params.studentId}/education-history`,
        {
          method: 'POST',
          body: {
            data: {
              type: 'education-history',
              attributes: {
                applicationId: params.applicationId,
                schoolName: params.schoolName,
                educationLevel: params.educationLevel,
                educationCountry: params.educationCountry,
                credentialReceived: params.credentialReceived,
                startDate: params.startDate.substring(0, 7) + '-01',
                endDate: params.endDate.substring(0, 7) + '-01',
                ...(params.degree && { degree: params.degree }),
                ...(params.fieldOfStudy && { fieldOfStudy: params.fieldOfStudy }),
                ...(params.gradeSchemeId && { gradeSchemeId: params.gradeSchemeId }),
                ...(params.gradeSchemeName && { gradeSchemeName: params.gradeSchemeName }),
                ...(transcriptPayload && { transcriptFile: transcriptPayload }),
                ...(certificatePayload && { certificateFile: certificatePayload }),
              },
            },
          },
          isExpectedResponse: isPostExpectedResponse,
        }
      );

      await uploadFilesToS3(response, params, s3UploadAsync);

      const hasFiles = params.transcriptFile || params.certificateFile;
      if (hasFiles) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
      }

      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['application', params.applicationId] }),
        queryClient.invalidateQueries({ queryKey: ['entity-history', 'application', params.applicationId] }),
      ]);

      return response;
    },
  });

  return {
    createEducationHistory: mutateAsync,
    isCreatingEducationHistory: isLoading,
    createEducationHistoryError: error,
  };
}

function isPostExpectedResponse(res: unknown): res is PostEducationHistoryResponseBody {
  return Boolean(res && typeof res === 'object' && hasKey('data', res));
}

type PresignedEntry = { upload?: { url: string; fields: Record<string, string> } };

async function uploadFilesToS3(
  response: PostEducationHistoryResponseBody,
  params: CreateEducationHistoryParams,
  s3UploadAsync: (p: { file: File; url: string; fields: Record<string, string> }) => Promise<unknown>
) {
  const filesMap = response.data.meta?.files as Record<string, PresignedEntry> | undefined;
  if (!filesMap) return;

  const uploads: Promise<unknown>[] = [];

  if (params.transcriptFile) {
    const entry = filesMap['transcript'];
    if (entry?.upload) {
      uploads.push(
        s3UploadAsync({
          file: params.transcriptFile.fileObject,
          url: entry.upload.url,
          fields: entry.upload.fields,
        })
      );
    }
  }

  if (params.certificateFile) {
    const entry = filesMap['certificate'];
    if (entry?.upload) {
      uploads.push(
        s3UploadAsync({
          file: params.certificateFile.fileObject,
          url: entry.upload.url,
          fields: entry.upload.fields,
        })
      );
    }
  }

  await Promise.all(uploads);
}
